import React, { forwardRef, KeyboardEventHandler, ReactElement } from 'react';
import './NewInputCheckbox.css';
import Label from '../Label';
import plus from '../../assets/icons/InputCheckbox/Plus.svg';
import check from '../../assets/icons/InputCheckbox/check.svg';
import CardContainer from '../CardContainer';
import Row from '../../foundational/Layout/Row';
import Icon from '../Icon';
import resolvedClassName from '../../utils/resolvedClassName';
import correctIcon from '../../assets/icons/icon-correct.svg';
import incorrectIcon from '../../assets/icons/icon-incorrect.svg';
export interface INewInputCheckboxProps {
	className?: string;
	labelClass?: string;
	value: boolean;
	setValue: THandlerFunction<boolean>;
	label?: string | ReactElement | ReactElement[];
	name?: string;
	id?: string;
	disabled?: boolean;
	tabIndex?: number;
	variant?: 'checkbox' | 'bubble' | 'quiz';
	status?: undefined | 'correct' | 'incorrect';
	checkboxId?: string;
}

const NewInputCheckbox = forwardRef(
	(
		{
			className = '',
			labelClass = '',
			value = false,
			setValue = () => { },
			label,
			name = '',
			id = '',
			disabled = false,
			tabIndex = 0,
			variant = 'checkbox',
            status,
			checkboxId = '',
			...rest
		}: INewInputCheckboxProps,
		ref: any,
	) => {
		const handleOnClickForARIA = () => {
			setValue(!value);
		};
		const handleSpaceKeyDownForARIA: KeyboardEventHandler<HTMLDivElement> = (
			e,
		) => {
			if (e.key === ' ') {
				e.preventDefault();
				setValue(!value);
			}
		};
		const checkboxVariant = () => {
			return (
				<>
					<label
						htmlFor={id}
						className={`NewInputCheckbox ${className} ${disabled ? 'disabled' : ''}`}
					>
						<input
							id={id}
							ref={ref}
							type='checkbox'
							className='NewInputCheckbox__hidden-input'
							name={name}
							checked={value}
							onChange={(e) => setValue(e.target.checked)}
							disabled={disabled}
							{...rest}
						/>
						<div
							className='NewInputCheckbox__alignment-container'
							onClick={handleOnClickForARIA}
							onKeyDown={handleSpaceKeyDownForARIA}
						>
							<span
								className={`NewInputCheckbox__checkbox`}
								aria-checked={value}
								role='checkbox'
								id={checkboxId}
							>
								<span className='NewInputCheckbox__checkbox__mark' />
							</span>
							<Label className={labelClass}>
								{label}
							</Label>
						</div>
					</label>
				</>
			);
		};

		const bubbleVariant = () => {
			return (
				<>
					<label
						htmlFor={id}
						className={`NewInputCheckbox ${className}`}
					>
						<input
							id={id}
							ref={ref}
							type='checkbox'
							className='NewInputCheckbox__hidden-input'
							name={name}
							checked={value}
							onChange={(e) => setValue(e.target.checked)}
							{...rest}
						/>
						<div
							className={`NewInputCheckbox__alignment-container--bubble ${value ? 'NewInputCheckbox__bubbleMarked' : ''} `}
							onClick={handleOnClickForARIA}
							onKeyDown={handleSpaceKeyDownForARIA}
						>
							<Label className={`NewInputCheckbox__bubbleText ${labelClass}`}>
								{label}
							</Label>
							<span
								className={`NewInputCheckbox__checkImage`}
								aria-checked={value}
								role='checkbox'
								id={checkboxId}
							>
								{value ? <img src={check} alt='Checkmark' /> : <img src={plus} alt='Plus Sign'/>}
							</span>
						</div>
					</label>
				</>
			);
		}

		const quizVariant = () => {
			const finalStatus = status;
			return (
				<>
					<CardContainer
						className={`NewInputCheckbox mb-4 NewInputCheckbox__quiz-container ${finalStatus ? (finalStatus === 'correct' ? 'NewInputCheckbox__correct' : 'NewInputCheckbox__incorrect') : ''}`}
						borderSettings={value ? { borderColor: 'blue-creditone' } : { borderColor: 'grey-chalice-50' }}
						onClick={() => !disabled && setValue(!value)}
						onKeyDown={(e) => {
							if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
								e.preventDefault();
								setValue(!value);
							}
						}}
						tabIndex={disabled ? -1 : 0}
						role="checkbox"
						aria-checked={value}>
						<Row justify='start'>
							<label
								htmlFor={id}
								className={resolvedClassName(
									'NewInputCheckbox',
									className,
									disabled && 'disabled',
								)}
								style={{ width: '100%', cursor: 'pointer' }}>
								<input
									id={id}
									ref={ref}
									type='checkbox'
									className='NewInputCheckbox__hidden-input'
									name={name}
									checked={value}
									onChange={(e) => setValue(e.target.checked)}
									disabled={disabled}
									aria-disabled={disabled}
									{...rest}
								/>
								<div className='NewInputCheckbox__alignment-container'>
									<span
										className={`NewInputCheckbox__checkbox ${(!disabled && status === 'incorrect')
											? 'NewInputCheckbox__checkbox__error'
											: ''} 
											${finalStatus
												? `NewInputCheckbox__checkbox__status`
												: ''}`}
										id={checkboxId}
									>
										{finalStatus && (finalStatus === 'correct' ? (
											<Icon customSize={24} src={correctIcon} alt="Correct" />
										) : (
											<Icon customSize={20} src={incorrectIcon} alt="Incorrect" />
										)
										)}
										{!finalStatus && <span className='NewInputCheckbox__checkbox__mark' />}
									</span>
									<Label className={labelClass}>
										{label}
									</Label>
								</div>
							</label>
						</Row>
					</CardContainer>
				</>
			);
		};

		const getButtonVariant = () => {
			switch (variant) {
				case 'bubble':
					return bubbleVariant();
				case 'quiz':
					return quizVariant();
				default:
					return checkboxVariant();
			}
		};

		return (
			<>
				{getButtonVariant()}
			</>
		);
	},
);

NewInputCheckbox.displayName = 'NewInputCheckbox';

export default NewInputCheckbox;
